import { Suspense, useCallback, useState } from 'react';
import * as THREE from 'three';
import { type ThreeEvent } from '@react-three/fiber';
import { Billboard } from '@react-three/drei';
import { Svg } from './Svg';
import { ViewerLayer } from './ViewerLayer';
import { useOnClick } from './useOnClick';
import { RENDER_ORDER_MARKER } from './RenderOrder';

export interface SvgMarkerProps {
  position: Parameters<THREE.Vector3['set']>;
  rotation?: Parameters<THREE.Euler['set']>;
  svgComp: string;
  scale?: number;
  markerHeight?: number;
  hoveredComp?: string;
  hoveredScale?: number;
  onMarkerClick?: () => void;
}

const defaultMarkerHeight = 0;
const defaultScale = 1;

const scaleAdjustmentFactor = 0.05;

export const SvgMarker = ({
  position,
  rotation,
  onMarkerClick,
  svgComp,
  scale = defaultScale,
  markerHeight = defaultMarkerHeight,
  hoveredComp,
  hoveredScale = defaultScale,
}: SvgMarkerProps) => {
  const shouldHandle = useCallback((event: ThreeEvent<MouseEvent>) => {
    const { altKey, ctrlKey, metaKey, shiftKey, button } = event;
    const isLeftButton = button === 0;
    if (!isLeftButton || altKey || ctrlKey || metaKey || shiftKey) {
      return false;
    }
    return true;
  }, []);

  const onClick = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();
      if (onMarkerClick) {
        onMarkerClick();
      }
    },
    [onMarkerClick]
  );

  const { onPointerDown, onPointerUp } = useOnClick({ onClick, shouldHandle });

  const [hovered, setHovered] = useState(false);

  const referenceCallback = useCallback((rootNode: THREE.Object3D | null) => {
    function enableLayers(node: THREE.Object3D | null) {
      if (node != null) {
        node.layers.enable(ViewerLayer.AutoPickable);
        node.layers.enable(ViewerLayer.Default);
        node.layers.enable(ViewerLayer.SphericalViewerLayer);
        node.renderOrder = RENDER_ORDER_MARKER;
        node.children.forEach((childNode) => enableLayers(childNode));
      }
    }

    enableLayers(rootNode);
  }, []);

  const overCallback = useCallback(() => {
    setHovered(() => true);
  }, []);

  const outCallback = useCallback(() => {
    setHovered(false);
  }, []);

  const actualScale = hovered ? hoveredScale : scale;

  return (
    <Suspense>
      <object3D
        ref={referenceCallback}
        position={position}
        rotation={rotation}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerOver={overCallback}
        onPointerOut={outCallback}
      >
        <Billboard
          position={[0, 0, actualScale * markerHeight]}
          scale={actualScale * scaleAdjustmentFactor}
        >
          <Svg src={hovered && hoveredComp ? hoveredComp : svgComp} />
        </Billboard>
      </object3D>
    </Suspense>
  );
};
